import { PaymentOrder } from '../../types';
import { resolveCurrencyCode } from '../../utils/currencyUtils';

interface ProviderInfo {
  name?: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
}

interface BankInfo {
  name?: string;
  accountNumber?: string;
}

const col = (v: any, w: number) => String(v ?? '').slice(0, w).padEnd(w);

/** Fixed-width item table with the full requisition data */
const itemsTable = (paymentOrder: PaymentOrder, headers: string[]): string => {
  const widths = [4, 26, 6, 8, 8, 10, 12, 10];
  const headerLine = headers.map((h, i) => col(h, widths[i])).join('| ');
  const rows = (paymentOrder.items || []).map((item: any) =>
    [
      col(item.no, widths[0]), col(item.details, widths[1]), col(item.qty, widths[2]),
      col(item.rob, widths[3]), col(item.appQty, widths[4]),
      col(Number(item.unitPrice || 0).toLocaleString(), widths[5]),
      col(Number(item.total || 0).toLocaleString(), widths[6]),
      col(item.status || 'pending', widths[7]),
    ].join('| ')
  ).join('\n');
  return `${headerLine}\n${'-'.repeat(96)}\n${rows}`;
};

export const generateEnglishTemplate = (
  paymentOrder: PaymentOrder,
  provider: ProviderInfo | null,
  bank: BankInfo | null
): string => {
  const paymentMethodDisplay = {
    'bank_transfer': 'Bank Transfer',
    'check': 'Check',
    'cash': 'Cash',
    'credit_card': 'Credit Card'
  }[paymentOrder.paymentMethod];

  return `
PETROMARINE OFFSHORE PROJECTS SUPPORT CO.

                                        Purchase Order

Date: ${new Date(paymentOrder.date).toLocaleDateString('en-US', { 
  year: 'numeric', 
  month: 'long', 
  day: 'numeric' 
})}                                                    Requisition No: ${paymentOrder.referenceNo || 'N/A'}

TO: ${provider?.name || '[Provider Name]'}
    ${provider?.address || '[Provider Address]'}
    Contact: ${provider?.contactPerson || '[Contact Person]'}
    Phone: ${provider?.phone || '[Phone Number]'}

${'-'.repeat(96)}
${itemsTable(paymentOrder, ['No', 'Item', 'Qty', 'ROB', 'App Qty', 'Unit Prc', 'Total', 'Status'])}
${'-'.repeat(96)}

                                                        Subtotal: ${resolveCurrencyCode(paymentOrder.currency)} ${paymentOrder.subtotal.toLocaleString()}
                                                           Taxes: ${resolveCurrencyCode(paymentOrder.currency)} ${paymentOrder.taxes.toLocaleString()}
                                                        Discount: ${resolveCurrencyCode(paymentOrder.currency)} ${paymentOrder.discount.toLocaleString()}
                                                           Total: ${resolveCurrencyCode(paymentOrder.currency)} ${paymentOrder.total.toLocaleString()}

Comments or Additional Instructions:
${paymentOrder.comments || 'None'}

${paymentOrder.additionalInstructions || ''}

Payment Method: ${paymentMethodDisplay}
${paymentOrder.paymentMethod === 'bank_transfer' && bank ? `Bank: ${bank.name} (${bank.accountNumber})` : ''}
${paymentOrder.paymentMethod === 'check' && paymentOrder.checkNumber ? `Check Number: ${paymentOrder.checkNumber}` : ''}

                                                    Authorized by:

Signature: ${paymentOrder.authorizedBy.signature}
Name: ${paymentOrder.authorizedBy.name}
Job Title: ${paymentOrder.authorizedBy.jobTitle}

${'-'.repeat(80)}
Address: Office No. 101, Building No. 3, Street No. 983, Salwa Road, Alexandria, Egypt
Tel: +20 1 069 069 300      Fax: +20 3 569 5300
E-mail: operations@petromarine.net      Website: www.petromarine.net
`;
};

export const generateArabicTemplate = (
  paymentOrder: PaymentOrder,
  provider: ProviderInfo | null,
  bank: BankInfo | null
): string => {
  const paymentMethodDisplay = {
    'bank_transfer': 'تحويل بنكي',
    'check': 'شيك',
    'cash': 'نقدي',
    'credit_card': 'بطاقة ائتمان'
  }[paymentOrder.paymentMethod];

  return `
شركة بتروماري للمشاريع البحرية

                                        أمر شراء

التاريخ: ${new Date(paymentOrder.date).toLocaleDateString('ar-EG', { 
  year: 'numeric', 
  month: 'long', 
  day: 'numeric' 
})}                                                    رقم طلب الشراء: ${paymentOrder.referenceNo || 'غير متوفر'}

إلى: ${provider?.name || '[اسم المورد]'}
     ${provider?.address || '[عنوان المورد]'}
     جهة الاتصال: ${provider?.contactPerson || '[شخص الاتصال]'}
     الهاتف: ${provider?.phone || '[رقم الهاتف]'}

${'-'.repeat(96)}
${itemsTable(paymentOrder, ['رقم', 'الصنف', 'الكمية', 'ROB', 'المعتمد', 'سعر الوحدة', 'الإجمالي', 'الحالة'])}
${'-'.repeat(96)}

                                                    المجموع الفرعي: ${resolveCurrencyCode(paymentOrder.currency)} ${paymentOrder.subtotal.toLocaleString()}
                                                         الضرائب: ${resolveCurrencyCode(paymentOrder.currency)} ${paymentOrder.taxes.toLocaleString()}
                                                          الخصم: ${resolveCurrencyCode(paymentOrder.currency)} ${paymentOrder.discount.toLocaleString()}
                                                   المجموع الكلي: ${resolveCurrencyCode(paymentOrder.currency)} ${paymentOrder.total.toLocaleString()}

التعليقات أو التعليمات الإضافية:
${paymentOrder.comments || 'لا يوجد'}

${paymentOrder.additionalInstructions || ''}

طريقة الدفع: ${paymentMethodDisplay}
${paymentOrder.paymentMethod === 'bank_transfer' && bank ? `البنك: ${bank.name} (${bank.accountNumber})` : ''}
${paymentOrder.paymentMethod === 'check' && paymentOrder.checkNumber ? `رقم الشيك: ${paymentOrder.checkNumber}` : ''}

                                                        معتمد من:

التوقيع: ${paymentOrder.authorizedBy.signature}
الاسم: ${paymentOrder.authorizedBy.name}
المسمى الوظيفي: ${paymentOrder.authorizedBy.jobTitle}

${'-'.repeat(80)}
العنوان: مكتب رقم 101، مبنى رقم 3، شارع رقم 983، طريق صلوى، الإسكندرية، مصر
تليفون: +20 1 069 069 300      فاكس: +20 3 569 5300
البريد الإلكتروني: operations@petromarine.net      الموقع الإلكتروني: www.petromarine.net
`;
};

export const getPrintHtml = (
  content: string,
  orderNumber: string,
  language: 'en' | 'ar'
): string => `
<html>
  <head>
    <title>Payment Order - ${orderNumber}</title>
    <style>
      body { 
        font-family: 'Courier New', monospace; 
        line-height: 1.4; 
        margin: 40px;
        direction: ${language === 'ar' ? 'rtl' : 'ltr'};
        font-size: 12px;
      }
      @media print {
        body { margin: 20px; }
      }
      .header {
        text-align: center;
        font-weight: bold;
        margin-bottom: 20px;
      }
      .content {
        white-space: pre-wrap;
        font-family: 'Courier New', monospace;
      }
    </style>
  </head>
  <body>
    <div class="content">${content}</div>
  </body>
</html>
`;
